import { Component, signal } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { CrusherDataService } from 'src/app/services/crusher-data.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { CrusherDataImportResult, CrusherDataPreview } from 'src/app/bo/crusher-data.models';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { PreviewDialogComponent } from './preview-dialog.component';

@Component({
  selector: 'app-crusher-data-import',
  templateUrl: './crusher-data-import.component.html',
  styleUrls: ['./crusher-data-import.component.scss'],
  standalone: true,
  imports: [ButtonComponent]
})
export class CrusherDataImportComponent {
  isUploading = signal(false);
  isPreviewing = signal(false);
  progress = signal(0);
  result = signal<CrusherDataImportResult | null>(null);
  error = signal<string | null>(null);

  private pendingFile: File | null = null;
  private pendingColumnsToAdd: string[] = [];

  constructor(
    private readonly crusherDataService: CrusherDataService,
    private readonly fonctionsService: FonctionsService,
    private readonly utilService: UtilisateurService,
    private readonly dialog: MatDialog
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.error.set('Please select a CSV file.');
      this.fonctionsService.launch_toast(false, false, 'Please select a CSV file.');
      return;
    }

    this.pendingFile = file;
    this.startPreview(file);
    input.value = '';
  }

  triggerFileInput(): void {
    const input = document.getElementById('csv-file-input') as HTMLInputElement;
    input.click();
  }

  private getSiteId(): number | undefined {
    return this.utilService.currentSite()?.siteId;
  }

  private startPreview(file: File): void {
    this.isPreviewing.set(true);
    this.isUploading.set(false);
    this.progress.set(0);
    this.result.set(null);
    this.error.set(null);

    const siteId = this.getSiteId();
    if (!siteId) {
      this.isPreviewing.set(false);
      const message = 'No site selected. Please select a site before importing.';
      this.error.set(message);
      this.fonctionsService.launch_toast(false, false, message);
      return;
    }

    this.crusherDataService.previewCsv(file, siteId).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.Response) {
          const body = event.body as CrusherDataPreview;
          this.isPreviewing.set(false);

          if (body.errors && body.errors.length > 0) {
            this.error.set(body.errors.join('. '));
            this.fonctionsService.launch_toast(false, false, body.errors.join('. '));
            return;
          }

          this.pendingColumnsToAdd = body.newColumns.map(c => c.columnName);
          this.openPreviewDialog(body);
        }
      },
      error: (err) => {
        this.isPreviewing.set(false);
        const message = err.error?.errors?.[0] || err.message || 'Preview failed. Please try again.';
        this.error.set(message);
        this.fonctionsService.launch_toast(false, false, message);
      }
    });
  }

  private openPreviewDialog(preview: CrusherDataPreview): void {
    const dialogRef = this.dialog.open(PreviewDialogComponent, {
      width: '900px',
      data: preview,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed && this.pendingFile) {
        this.doImport(this.pendingFile);
      }
      this.pendingFile = null;
    });
  }

  private doImport(file: File): void {
    this.isUploading.set(true);
    this.progress.set(0);
    this.result.set(null);
    this.error.set(null);

    const siteId = this.getSiteId();
    if (!siteId) {
      this.isUploading.set(false);
      const message = 'No site selected. Please select a site before importing.';
      this.error.set(message);
      this.fonctionsService.launch_toast(false, false, message);
      return;
    }

    this.crusherDataService.importCsv(file, this.pendingColumnsToAdd, siteId).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.progress.set(Math.round((100 * event.loaded) / event.total));
        } else if (event.type === HttpEventType.Response) {
          const body = event.body as CrusherDataImportResult;
          this.result.set(body);
          this.isUploading.set(false);
          this.progress.set(100);

          if (body.errors && body.errors.length > 0) {
            this.fonctionsService.launch_toast(false, false, body.errors.join('. '));
          } else {
            this.fonctionsService.launch_toast(true);
          }
        }
      },
      error: (err) => {
        this.isUploading.set(false);
        this.progress.set(0);
        const message = err.error?.errors?.[0] || err.message || 'Import failed. Please try again.';
        this.error.set(message);
        this.fonctionsService.launch_toast(false, false, message);
      }
    });
  }
}
